/**
 * 출하 검사 항목 — SignageOne 을 **납품하기 전에 제품이 약속대로 도는지 검증하는** 점검표.
 *
 * **검증만 담는다.** 교육·문서 전달·계정 인계·합의서 서명 같은 인수인계 절차는 여기 없다
 * (2026-09-28 사용자 지시로 걷었다) — 그것은 제품의 상태를 묻는 것이 아니라 사람이 사람에게
 * 넘기는 일이고, 성적서에 섞이면 "무엇이 합격이라는 말인가" 가 흐려진다.
 *
 * 묶음은 세 층이다.
 * - **환경** — 설치 환경·서버. 기능 검사가 의미를 가지려면 먼저 서 있어야 하는 것.
 * - **기능별 이상 여부** — 계정·콘텐츠·화면·편성·모니터링·에이전트. 콘솔 메뉴 순서를 따라
 *   고객이 인수 후 할 일을 검사자가 먼저 한 번씩 해 보고, 막아야 할 입력이 막히는지도 본다.
 * - **품질** — 재생 품질·성능 최적화·장애 복구·보안. 기능 검사에서는 드러나지 않고
 *   시간을 재거나, 오래 틀어 두거나, 일부러 깨 봐야 보이는 것.
 *
 * ## 항목마다 **누가 판정하는가**(`verify`)를 적는다
 *
 * 검사가 며칠로 늘어지는 이유는 늘 같다 — 앉아서 끝낼 수 있는 것과 기기 앞에 서야 하는 것을
 * 뒤섞어 놓고 순서대로 내려가기 때문이다. 그래서 항목마다 둘 중 하나를 적는다.
 *
 *   `auto`   판정 근거가 API 응답·설정 값·DB 상태·브라우저에서 보이는 것으로 정해진다.
 *            도구가 대신 돌려 같은 답을 낼 수 있다(사람이 눌러 확인해도 결과는 같다).
 *   `manual` 사람이 있어야 한다 — 기기 전원을 뽑고, 화면 앞에 서서 눈으로 보고, 다른 망에서
 *            접속하고, 엑셀로 열어 보는 것. 도구가 흉내 내면 "합격" 이 거짓이 된다.
 *
 * **애매하면 `manual` 이다.** 판정의 일부라도 사람 눈에 기대는 항목을 `auto` 로 적으면, 도구가
 * 통과시킨 항목이 현장에서 깨진다 — 검사표가 있는 이유가 그것을 막는 것이다.
 *
 * **합격 기준은 코드와 문서가 약속하는 수치에서 뽑았다.** 편성 반영 45초는 폴링 30초 + 해석
 * 캐시 10초(`PLAYER_POLL_SEC`·`RESOLVE_CACHE_SEC`, `docs/PLAN.md` §8.1 SLO), 온라인 판정 90초는
 * 폴링 × 3, API 300ms 는 부하 게이트(§8)다. 제품에 없는 것을 있는 것처럼 검사하지 않는다 —
 * 긴급 오버라이드 발령·서비스 토큰 발급은 콘솔 화면이 없어 API 로 하라고 적었고, 트랜스코드가
 * 없으니 콘텐츠 규격은 올리는 쪽이 맞춰야 한다고 적었다.
 *
 * 문서(`docs/SECURITY.md` §5, `docs/OPERATIONS.md` §1·§5)의 점검 목록과 겹치는 항목은
 * **일부러 겹쳐 두었다.** 저쪽은 운영자가 계속 도는 주기 점검이고 이쪽은 인도 시점에
 * 한 번 서명하는 성적서다 — 같은 항목이라도 증거를 남기는 자리가 다르다.
 */

/** 검사 판정. `undefined` 는 아직 보지 않은 것이다 — '해당 없음' 과 구분한다. */
export type Verdict = 'pass' | 'fail' | 'na';

export const VERDICT_LABEL: Readonly<Record<Verdict, string>> = {
  pass: '합격',
  fail: '불합격',
  na: '해당 없음',
};

export interface InspectionItem {
  /**
   * 저장에 쓰는 이름. 항목을 지우거나 순서를 바꿔도 남은 판정이 엉뚱한 줄에 붙지 않게 한다.
   * **뜻이 바뀐 항목은 새 이름을 준다** — 같은 이름을 다른 검사에 다시 쓰면 지난 판정이 붙는다.
   */
  id: string;
  title: string;
  /** 어떻게 확인하는가 — 검사자가 그대로 따라 할 수 있을 만큼 구체적으로. */
  how: string;
  /**
   * 무엇이면 합격인가.
   *
   * 방법과 따로 둔다. "확인한다" 로 끝나는 항목은 검사자마다 합격선이 달라지고, 성적서를
   * 받은 사람은 무엇을 보고 합격이라 했는지 알 수 없다. 잴 수 있는 것은 수치로 적는다.
   */
  criteria: string;
  /**
   * 하나라도 불합격이면 **출하할 수 없는** 항목.
   *
   * 전부 필수로 두면 필수라는 말이 의미를 잃는다. 여기 표시된 것은 "이게 안 되면 현장에
   * 설치해 봐야 다시 가져와야 한다" 뿐이다.
   */
  critical?: boolean;
  /**
   * 누가 판정하는가. **모든 항목이 하나를 갖는다** — 기본값을 두면 새 항목이 조용히 한쪽으로 들어간다.
   *
   * `auto` 는 "사람이 안 봐도 된다" 가 아니라 "**도구가 같은 답을 낼 수 있다**" 는 뜻이다.
   * 판정 근거가 눈·손·다른 망에 조금이라도 걸려 있으면 `manual` 이다(파일 머리말 참고).
   */
  verify: 'auto' | 'manual';
  /**
   * 기기·현장이 있어야 하는가.
   *
   * `verify: 'manual'` 중에서도 **이동과 실물이 필요한 것**을 따로 표시한다. 엑셀로 열어 보는 것과
   * 전원을 뽑아 보는 것은 같은 '사람' 이지만 일정에 묶는 방식이 다르다 — 앞의 것은 자리에서 되고
   * 뒤의 것은 방문 날을 잡아야 한다.
   *
   * `auto` 항목에는 붙지 않는다(시험이 본다).
   */
  where?: 'field';
}

export interface InspectionSection {
  id: string;
  title: string;
  /** 이 묶음에서 무엇을 보는지 한 줄. 성적서에도 그대로 인쇄된다. */
  intro: string;
  items: readonly InspectionItem[];
}

export const INSPECTION_SECTIONS: readonly InspectionSection[] = [
  /* ───────────────────────────── 환경 ───────────────────────────── */
  {
    id: 'env',
    title: '설치 환경 · 서버',
    intro: '서버가 떠 있는가가 아니라, 납품 주소·시각·자원·재기동까지 운영 조건을 갖췄는가를 본다.',
    items: [
      {
        id: 'env-health',
        verify: 'auto',
        title: '서버 준비 상태가 정상이다',
        how: '콘솔 [시스템] 의 서버 상태를 보고, 납품 주소의 /health/ready 를 브라우저로 연다.',
        criteria: '상태 ‘정상’ · 데이터베이스 ok · 서비스 활성화 ‘켜짐’ · /health/ready 응답 200.',
        critical: true,
      },
      {
        id: 'env-url',
        verify: 'manual',
        title: '납품 주소가 사무실 망과 화면 망 양쪽에서 HTTPS 로 열린다',
        how: '인수처 사무실 PC 와 화면 기기가 놓일 망의 PC 에서 각각 납품 주소를 연다. 인증서 만료일을 비고에 적는다.',
        criteria: '두 망 모두 인증서 경고 없이 로그인 화면이 뜬다. 개발용 IP·임시 주소가 아니다.',
        critical: true,
      },
      {
        id: 'env-media-origin',
        verify: 'auto',
        title: '미디어가 납품 주소와 같은 오리진에서 나간다',
        how: '콘텐츠 상세에서 영상 미리보기를 튼 채 브라우저 개발자 도구 [네트워크] 에서 미디어 요청의 최종 주소를 본다.',
        criteria: '미디어 요청이 납품 주소와 같은 도메인에서 끝난다. 다른 도메인·내부 IP 로 넘어가면 불합격 — 콘솔에서는 보여도 화면에서는 재생되지 않는다.',
        critical: true,
      },
      {
        /*
         * 인수인계 항목을 걷을 때 이것만 남겼다. 파일을 고르고 절차를 인계하는 일은 사람의 것이지만,
         * **앞단 한도에 막히는지는 제품 쪽 사실**이고 현장에 나가서 발견하면 늦다 — 공개 주소가
         * Cloudflare 뒤면 100MB 에서 413 이다. 그 벽이 있는지만 묻는 항목으로 좁혔다.
         */
        id: 'env-upload-limit',
        verify: 'auto',
        title: '현장에서 쓸 크기의 파일이 납품 주소로 올라간다',
        how: '현장에서 쓸 가장 큰 영상과 같은 크기의 파일을 납품 주소(고객이 실제로 쓸 주소)로 올린다. 올라간 크기를 비고에 적는다. 앞단이 Cloudflare 면 100MB 에서 413 이고, 서버에 직접 닿는 사내 주소는 nginx 설정값(기본 2GB)까지 받는다.',
        criteria: '413 없이 등록된다. 앞단 프록시·CDN 한도에 걸리면 어느 크기에서 막히는지 비고에 적는다 — 그 벽을 모르고 납품하면 현장에서 “큰 영상만 안 올라간다” 로 나타난다.',
      },
      {
        id: 'env-clock',
        verify: 'auto',
        title: '서버 시각이 표준시와 맞다',
        how: '서버 호스트에서 timedatectl(또는 운영체제 시간 설정)로 NTP 동기화를 확인하고 표준시와 대조한다.',
        criteria: 'NTP 동기화 켜짐, 오차 1분 이내. 화면은 서버 시각으로 보정하므로 서버 시각이 틀리면 전 화면의 시간대 편성이 함께 밀린다.',
        critical: true,
      },
      {
        id: 'env-autostart',
        verify: 'manual',
        title: '서버 호스트를 재부팅해도 서비스가 저절로 뜬다',
        how: '서버 호스트를 실제로 재부팅하고 아무것도 만지지 않은 채 기다린다.',
        criteria: '5분 안에 콘솔 로그인이 되고 [시스템] 이 정상이다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'env-resources',
        verify: 'auto',
        title: '서버 사양과 디스크 여유가 운용 기간을 감당한다',
        how: '서버의 메모리·디스크 여유(free -h · df -h)와 콘텐츠 총량·월 증가 예상(자산 + 재생 증빙)을 비고에 적는다.',
        criteria: '메모리 2GB 이상(권장 4GB). 디스크 여유가 1년치 증가 예상보다 크다 — 운용 중 디스크를 감시하는 기능은 없다.',
      },
      {
        id: 'env-log-rotation',
        verify: 'auto',
        title: '서버 로그가 한없이 쌓이지 않는다',
        how: '설치 패키지로 깔았으면 로그 정리 설정을, 직접 구성한 docker compose 면 로그 드라이버의 크기 제한을 확인한다.',
        criteria: '로그 크기 상한이 설정되어 있다(패키지 기본: Docker 판 서비스당 20MB×5, 네이티브 판 매일 50MB 초과분 정리).',
      },
    ],
  },

  /* ─────────────────────────── 기능별 이상 여부 ─────────────────────────── */
  {
    id: 'auth',
    title: '기능 — 계정 · 권한',
    intro: '사람마다 할 수 있는 일이 역할대로 갈리고, 잘못 들어오려는 시도가 막히는지 본다.',
    items: [
      {
        id: 'auth-login',
        verify: 'auto',
        title: '로그인 · 로그아웃이 동작한다',
        how: '올바른 계정, 틀린 비밀번호, 없는 이메일로 각각 로그인해 본다. 로그아웃 후 브라우저 뒤로 가기를 누른다.',
        criteria: '올바른 계정만 들어간다. 틀린 비밀번호와 없는 이메일은 같은 문구로 거절된다. 로그아웃 뒤에는 콘솔 내용이 보이지 않는다.',
        critical: true,
      },
      {
        id: 'auth-lockout',
        verify: 'auto',
        title: '비밀번호를 연달아 틀리면 계정이 잠긴다',
        how: '검사용 계정으로 틀린 비밀번호를 10회 넣는다. 시스템 계정으로 [사용자] 목록을 본다.',
        criteria: '10회 실패 뒤 "N분 후 다시 시도하세요" 로 막히고, 목록에 ‘잠김’ 배지가 뜬다. 15분 뒤 다시 로그인된다.',
      },
      {
        id: 'auth-new-user',
        verify: 'auto',
        title: '사용자를 추가하면 첫 로그인에서 비밀번호를 바꾸게 한다',
        how: '[사용자] ▸ [사용자 추가] 로 계정을 만들고 임시 비밀번호로 로그인한다. 새 비밀번호로 8자 미만을 넣어 본다.',
        criteria: '임시 비밀번호는 한 번만 보인다. 첫 로그인은 비밀번호 변경 화면만 열리고, 8자 미만은 거절된다.',
      },
      {
        id: 'auth-roles',
        verify: 'auto',
        title: '역할마다 보이는 메뉴와 할 수 있는 일이 다르다',
        how: '역할 다섯(조회 · 감사 · 콘텐츠 관리 · 관리자 · 시스템) 계정으로 각각 로그인해 사이드바와 각 화면의 만들기·저장 단추를 본다. 고객사가 실제로 쓸 역할을 빠짐없이 본다 — 고객에게 "권한은 네 가지" 라고 말하면서 검사는 둘만 보면 안 된다.',
        criteria: '조회: 만들기·저장 단추 없음, 리포트·사용자 메뉴 없음. 감사: 리포트는 보이되 아무것도 바꿀 수 없고 활동 로그 메뉴는 없음. 콘텐츠 관리: 콘텐츠는 올리고 고치되 편성·화면은 못 바꿈. 관리자: 활동 로그·정비 모드 메뉴 없음, 사용자 목록에 시스템 계정이 안 보임. 시스템: 활동 로그·정비 모드 메뉴까지 모두 보임.',
        critical: true,
      },
      {
        id: 'auth-direct-url',
        verify: 'auto',
        title: '권한 없는 화면은 주소를 직접 쳐도 열리지 않는다',
        how: '관리자(admin) 역할 계정으로 주소창에 #/inspection · #/agent · #/server-packages 를 직접 넣는다. 공급사 자리(시스템 역할)만 여는 화면들이다.',
        criteria: '세 주소 모두 대시보드로 돌아가고 내용이 보이지 않는다.',
      },
      {
        id: 'auth-sessions',
        verify: 'auto',
        title: '다른 기기의 로그인을 끊을 수 있다',
        how: '같은 계정으로 다른 브라우저에서 로그인해 둔 뒤 [내 계정] ▸ [다른 기기 모두 로그아웃] 을 누른다. [사용자] 에서 다른 계정을 비활성화한다.',
        criteria: '다른 브라우저는 다음 동작에서 로그인 화면으로 간다. 비활성화한 사용자도 즉시 끊긴다.',
      },
      {
        id: 'auth-last-admin',
        verify: 'auto',
        title: '마지막 시스템 계정은 없앨 수 없다',
        how: '시스템(개발자 포함) 계정이 하나인 상태에서 그 계정을 강등·비활성화·삭제해 본다.',
        criteria: '셋 모두 거절된다. 시스템 계정이 사라져 아무도 시스템을 다룰 수 없는 상태가 되지 않는다.',
      },
    ],
  },
  {
    id: 'content',
    title: '기능 — 콘텐츠',
    intro: '고객이 인수 후 가장 먼저, 가장 자주 할 일이다. 올리고, 고치고, 내리는 전 과정을 한 번씩 해 본다.',
    items: [
      {
        id: 'con-image',
        verify: 'auto',
        title: '이미지를 올리면 바로 쓸 수 있다',
        how: '[콘텐츠] ▸ [콘텐츠 추가] ▸ [파일 업로드] 로 JPG · PNG 를 1건씩 올린다.',
        criteria: '목록에 ‘사용 가능’ 으로 뜨고 상세 미리보기가 원본과 같다.',
        critical: true,
      },
      {
        id: 'con-video',
        verify: 'manual',
        title: '영상을 올리면 미리보기에서 재생된다',
        how: '현장에서 쓸 MP4 영상을 올리고 콘텐츠 상세에서 재생한다.',
        criteria: '등록되고 소리·화면이 정상 재생된다.',
        critical: true,
      },
      {
        id: 'con-reject',
        verify: 'auto',
        title: '지원하지 않는 파일은 거절된다',
        how: 'PDF·HWP 같은 문서와, 확장자만 .mp4 로 바꾼 문서 파일을 올려 본다.',
        criteria: '둘 다 "지원하지 않는 파일 형식" 으로 거절되고 목록에 남지 않는다(형식은 확장자가 아니라 파일 내용으로 판정한다).',
      },
      {
        id: 'con-duplicate',
        verify: 'auto',
        title: '같은 파일을 다시 올려도 중복 저장되지 않는다',
        how: '이미 올린 파일을 이름만 바꿔 다시 올린다.',
        criteria: '새 콘텐츠가 생기지 않고 "이미 등록된 파일이라 기존 콘텐츠를 사용합니다." 가 뜬다.',
      },
      {
        id: 'con-web',
        verify: 'auto',
        title: '웹 URL 콘텐츠가 등록되고 내부망 주소는 막힌다',
        how: '[웹 URL 추가] 로 현장에서 쓸 웹페이지를 등록한다. 192.168.x.x · localhost 주소도 넣어 본다.',
        criteria: '공개 주소는 등록되어 목록에 뜬다. 내부망·localhost 주소는 거절된다.',
      },
      {
        id: 'con-template',
        verify: 'manual',
        title: '템플릿 콘텐츠를 만들고 고칠 수 있다',
        how: '[템플릿 만들기] 로 현장에서 쓸 템플릿(공지·프로모션·긴급알림·일정·대시보드)을 만든다. 하단 티커를 비우고 저장해 본다.',
        criteria: '편집기 미리보기대로 저장되고, 수정하면 목록 미리보기에도 반영된다. 티커가 비면 저장되지 않는다.',
      },
      {
        id: 'con-archive',
        verify: 'manual',
        title: '콘텐츠를 보관하면 트는 화면에서 바로 빠진다',
        how: '화면에서 재생 중인 콘텐츠를 상세에서 [보관] 하고 화면 앞에서 시간을 잰다. 다시 복원한다.',
        criteria: '45초 안에 그 콘텐츠가 화면에서 빠지고, 복원하면 다시 나온다. 잘못 나간 광고를 내리는 가장 빠른 길이다.',
        critical: true,
      },
      {
        id: 'con-delete-guard',
        verify: 'auto',
        title: '편성에 쓰는 콘텐츠는 삭제되지 않는다',
        how: '플레이리스트에 들어 있는 콘텐츠와 아무 데도 안 쓰는 콘텐츠를 각각 삭제해 본다.',
        criteria: '쓰는 것은 "편성에 사용 중인 자산은 삭제할 수 없습니다" 와 플레이리스트 이름이 뜨며 거절되고, 안 쓰는 것은 삭제된다.',
      },
      {
        id: 'con-usage',
        verify: 'auto',
        title: '콘텐츠 상세의 배포 현황이 실제와 맞다',
        how: '재생 중인 콘텐츠의 상세에서 "배포 현황 · 화면 N대" 를 보고 실제로 트는 화면 수와 대조한다.',
        criteria: '표시된 화면 수와 목록이 실제로 그 콘텐츠를 트는 화면과 같다.',
      },
    ],
  },
  {
    id: 'display',
    title: '기능 — 화면 등록 · 관리',
    intro: '실물 기기와 콘솔 목록이 한 줄씩 맞고, 콘솔에서 바꾼 설정이 기기에 그대로 적용되는지 본다.',
    items: [
      {
        id: 'dsp-pairing',
        verify: 'manual',
        title: '페어링 코드로 화면을 등록한다',
        how: '기기에 뜬 6자리 코드를 [화면] ▸ [화면 추가] 에 화면 이름·위치와 함께 넣는다. 이미 쓴 코드도 한 번 넣어 본다. 코드는 15분마다 새로 바뀌므로 기기 화면에 지금 떠 있는 값을 쓴다.',
        criteria: '등록 직후 기기의 코드 화면이 사라지고 콘솔에 온라인으로 뜬다. 이미 쓴 코드·틀린 코드는 거절된다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'dsp-inventory',
        verify: 'manual',
        title: '납품 기기의 수량 · 이름 · 일련번호가 목록과 일치한다',
        how: '동봉 기기 목록과 실물을 한 대씩 대조하고 성적서 기기 목록 표에 일련번호를 적는다. 기기 실물에 콘솔과 같은 이름 라벨을 붙인다.',
        criteria: '콘솔 화면 수 = 실물 수이고, 이름·위치가 현장 사람이 부르는 이름으로 들어갔다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'dsp-orientation',
        verify: 'manual',
        title: '회전 · 해상도 설정이 설치 방향과 맞다',
        how: '화면 상세 톱니 ▸ [화면 표시] 의 화면 회전과 기본 정보의 해상도를 실제 설치와 대조한다.',
        criteria: '세로로 세운 화면은 콘텐츠가 잘림·여백 없이 세로로 꽉 찬다. 보고된 해상도가 패널 해상도와 같다.',
        where: 'field',
      },
      {
        id: 'dsp-timezone',
        verify: 'auto',
        title: '화면의 시간대가 현장 기준이다',
        how: '화면 상세 기본 정보의 타임존을 본다. 콘솔에서는 볼 수만 있고, 바꿔야 하면 API(PATCH /api/v1/displays/:id)로 바꾼다.',
        criteria: '현장 표준시간대(국내는 Asia/Seoul)다.',
      },
      {
        id: 'dsp-capture',
        verify: 'manual',
        title: '원격으로 지금 화면을 볼 수 있다',
        how: '화면 상세를 열고 ‘현재 화면’ 의 [다시 캡처] 를 누른 뒤 실제 화면과 비교한다.',
        criteria: '보통 20~40초, 늦어도 90초 안에 실제 화면과 같은 그림이 뜬다(영상은 재생 위치로 표시). 그 전에는 전에 찍어 둔 사진이 찍은 시각과 함께 보인다 — 그것을 새 사진으로 착각하지 않는다.',
      },
      {
        id: 'dsp-trace',
        verify: 'auto',
        title: '‘편성 결정 근거’ 가 실제 편성과 같은 답을 한다',
        how: '화면 상세의 ‘편성 결정 근거’ 에서 판단 근거를 보고, 시각을 바꿔 다른 시간대의 결과도 본다.',
        criteria: '설정한 배정·스케줄·캠페인과 같은 결과와 근거가 나오고, 다른 시각을 고르면 그 시각의 편성이 나온다.',
      },
      {
        id: 'dsp-bulk',
        verify: 'auto',
        title: '여러 화면을 한 번에 바꿀 수 있다',
        how: '[화면] 목록에서 2대 이상을 골라 일괄 편성 배정·위치 적용을 한다.',
        criteria: '고른 화면에만 적용되고 나머지는 그대로다. [활동 로그] 에 대상 수와 함께 남는다.',
      },
      {
        id: 'dsp-hide-delete',
        verify: 'manual',
        title: '화면 숨기기 · 삭제가 의도대로 동작한다',
        how: '검사용 기기 1대로 [숨기기] 와 [삭제] 를 해 본다.',
        criteria: '숨긴 화면은 대시보드·모니터링 집계에서 빠지되 재생은 계속된다. 삭제하면 기기에 페어링 코드가 다시 뜬다.',
        where: 'field',
      },
      {
        id: 'dsp-maintenance',
        verify: 'manual',
        title: '정비 모드는 지정한 PIN 으로만 들어간다',
        how: '화면 상세 톱니 ▸ [정비 모드 PIN] 에 기본값(0000)이 아닌 PIN 을 저장한다. 기기에서 화면 두 번 탭(또는 F10) 후 틀린 PIN 5회, 맞는 PIN 순으로 넣는다.',
        criteria: '틀린 PIN 5회면 30초 잠긴다. 맞는 PIN 으로 들어가면 콘솔에 정비 모드 경고가 뜨고, 오른쪽 위 [×] 로 재생이 이어진다. 기본값 PIN 이 남은 기기가 없다.',
        critical: true,
        where: 'field',
      },
    ],
  },
  {
    id: 'schedule',
    title: '기능 — 편성',
    intro: '무엇을 언제 어디에 틀지는 이 제품의 핵심이다. 설정한 대로 나가는지 화면 앞에서 대조한다.',
    items: [
      {
        id: 'sch-playlist',
        verify: 'manual',
        title: '플레이리스트가 순서 · 노출 시간대로 반복된다',
        how: '[플레이리스트] 에 이미지 3개 이상과 영상 1개를 넣고 이미지의 노출 시간을 서로 다르게 준 뒤 화면에 배정해 두 바퀴 이상 본다.',
        criteria: '정한 순서와 시간(±1초)대로 나오고 마지막 뒤 처음으로 돌아온다. 영상은 노출 시간과 상관없이 끝까지 재생된 뒤 다음 항목으로 넘어간다.',
        critical: true,
      },
      {
        id: 'sch-schedule',
        verify: 'manual',
        title: '스케줄이 요일 · 시간대 규칙대로 바뀐다',
        how: '[스케줄] 에 ‘평소 편성’ 과 시간대 규칙 2개를 넣고 ‘하루 미리보기’ 를 본다. 규칙 경계 시각에 화면 앞에서 기다린다.',
        criteria: '미리보기와 실제 화면이 같고, 경계 시각에서 1분 안에 바뀐다. 규칙이 없는 시간에는 평소 편성이 나온다.',
        critical: true,
      },
      {
        id: 'sch-overnight',
        verify: 'auto',
        title: '자정을 넘는 시간대 규칙이 이어진다',
        how: '22:00~02:00 같은 규칙을 넣고 ‘하루 미리보기’ 와 화면 상세 ‘편성 결정 근거’ 에서 자정 이후 시각을 고른다.',
        criteria: '‘익일까지’ 로 표시되고 00:00~02:00 에도 그 규칙이 적용된다. 심야 운영이 없으면 해당 없음.',
      },
      {
        id: 'sch-campaign',
        verify: 'manual',
        title: '캠페인이 기간 · 대상 · 게재 시간대에만 덮인다',
        how: '[캠페인] 에서 재생 방식 ‘전체 대체’, 대상 ‘태그로 지정’, 가까운 종료 시각, 게재 시간대를 넣는다.',
        criteria: '그 태그의 화면에서, 게재 시간대에만 나온다. 종료 시각이 지나면 손대지 않아도 원래 편성으로 돌아온다.',
      },
      {
        id: 'sch-campaign-insert',
        verify: 'manual',
        title: '삽입 캠페인이 평소 편성 사이에 끼어 나온다',
        how: '재생 방식 ‘삽입’, 삽입 간격 3 으로 캠페인을 만들고 화면에서 한 바퀴 이상 센다.',
        criteria: '평소 콘텐츠 3개마다 캠페인 콘텐츠 1개가 나온다.',
      },
      {
        id: 'sch-direct',
        verify: 'manual',
        title: '바로 보내기가 즉시 덮이고 기한이 지나면 풀린다',
        how: '화면 상세 [바로 보내기] 로 ‘재생 기간’ 을 1시간으로 보낸다. 한 번은 기한 전에 해제하고, 한 번은 짧은 기한이 지나기를 기다린다.',
        criteria: '45초 안에 보낸 콘텐츠로 바뀐다. 해제하거나 기한이 지나면 원래 편성으로 돌아온다.',
      },
      {
        id: 'sch-emergency',
        verify: 'manual',
        title: '긴급 오버라이드를 발령하고 해제할 수 있다',
        how: '콘솔에는 발령 단추가 없다 — [API 정의] 의 emergencies 예시대로 시스템(system) 역할 서비스 토큰으로 전 화면 발령한다. 해제는 [캠페인] 화면의 [즉시 해제] 로 한다. 인수 담당자에게 발령 명령을 직접 실행해 보게 한다.',
        criteria: '45초 안에 모든 화면이 긴급 편성으로 바뀌고 알림 종에 표시된다. [즉시 해제] 뒤 45초 안에 원래 편성으로 돌아온다.',
        critical: true,
      },
      {
        id: 'sch-priority',
        verify: 'manual',
        title: '겹친 편성의 우선순위가 정해진 순서다',
        how: '한 화면에 화면 배정 · 캠페인 · 바로 보내기를 겹쳐 두고 위에서부터 하나씩 해제하며 ‘편성 결정 근거’ 와 화면을 본다.',
        criteria: '긴급 ▸ 바로 보내기 ▸ 캠페인 ▸ 화면 배정 ▸ 그룹 배정 순으로 이기고, 판단 근거가 같은 순서를 말한다.',
      },
      {
        id: 'sch-delete-guard',
        verify: 'auto',
        title: '쓰고 있는 플레이리스트 · 스케줄은 삭제되지 않는다',
        how: '화면에 배정된 플레이리스트와 스케줄을 삭제해 본다.',
        criteria: '둘 다 사용 중이라 거절되고, 화면 재생에 영향이 없다.',
      },
    ],
  },
  {
    id: 'monitoring',
    title: '기능 — 모니터링 · 리포트 · 기록',
    intro: '납품 후 문제를 먼저 발견하는 쪽이 고객이 되지 않도록, 상태를 보는 자리가 사실을 말하는지 본다.',
    items: [
      {
        id: 'mon-dashboard',
        verify: 'manual',
        title: '대시보드 수치가 실제와 맞다',
        how: '[대시보드] 의 전체 · 온라인 · 오프라인 · 배정 없음 수를 실물과 대조한다.',
        criteria: '모든 수가 실제 대수와 같다.',
        critical: true,
      },
      {
        id: 'mon-offline-detect',
        verify: 'manual',
        title: '꺼진 화면이 오프라인으로 표시되고 알림이 뜬다',
        how: '기기 1대의 랜선(또는 전원)을 뽑고 시간을 잰다. 다시 연결한다.',
        criteria: '2분 안에 오프라인으로 바뀌고 알림 종 숫자가 늘어난다(마지막 연결 90초 경과 + 콘솔 갱신 30초). 다시 연결하면 2분 안에 온라인으로 돌아온다.',
        where: 'field',
      },
      {
        id: 'mon-wall',
        verify: 'manual',
        title: '모니터링 화면이 전 화면의 실제 송출을 보여 준다',
        how: '[모니터링] 에서 전 화면 카드와 ‘문제만’ 필터를 보고, [배치 편집] 으로 위치 카드와 그 안의 화면을 옮긴 뒤 새로고침한다. 지점이 여럿이면 위치 카드가 지점마다 하나씩 섰는지 본다.',
        criteria: '카드 그림이 실제 화면과 같다. ‘문제만’ 은 오프라인·오류·빈 편성 화면만 남긴다. 바꾼 배치가 유지된다. 묶어 보면 위치마다 제목줄에 대수와 오프라인 수가 적힌다.',
      },
      {
        id: 'mon-search',
        verify: 'auto',
        title: '전체 검색으로 콘텐츠와 화면을 찾는다',
        how: '아무 화면에서 / 키를 누르고 콘텐츠 이름·화면 이름 2글자를 넣는다.',
        criteria: '검색칸으로 바로 가고 해당 콘텐츠·화면이 나오며, 누르면 상세로 간다.',
      },
      {
        id: 'mon-report',
        verify: 'auto',
        title: '재생 리포트가 실제 재생을 집계한다',
        how: '[리포트] 에서 검사 기간을 콘텐츠별 · 화면별 · 일자별로 조회한다.',
        criteria: '검사 중 튼 콘텐츠의 재생 건수가 잡히고, 온라인으로 재생한 모든 화면이 화면별 집계에 있다. 온라인인데 증빙이 없는 화면이 있으면 불합격.',
        critical: true,
      },
      {
        id: 'mon-csv',
        verify: 'manual',
        title: 'CSV 내보내기가 엑셀에서 바로 열린다',
        how: '[리포트] ▸ [CSV 내보내기] 로 받은 파일을 엑셀로 연다.',
        criteria: '한글이 깨지지 않고, 머리줄을 뺀 행 수가 같은 기간의 총 재생 횟수와 같다.',
      },
      {
        id: 'mon-audit',
        verify: 'auto',
        title: '활동 로그에 검사 중 한 일이 남는다',
        how: '시스템 계정으로 [활동 로그] 를 열어 오늘 한 로그인 · 콘텐츠 등록 · 편성 변경 · 사용자 추가를 찾는다(활동 로그는 시스템 계정 메뉴다).',
        criteria: '각 작업이 행위자 · 시각 · 대상과 함께 남아 있다.',
      },
    ],
  },
  {
    id: 'agent',
    title: '기능 — 에이전트 설치 · 업데이트',
    intro: '현장에서 기기를 더 늘리거나 버전을 올릴 때, 검사자 없이도 같은 결과가 나오는지 본다.',
    items: [
      {
        id: 'agt-install-token',
        verify: 'auto',
        title: '설치 토큰을 발급하고 폐기할 수 있다',
        how: '[정비 모드] ▸ [에이전트] ▸ [설치 토큰 발급] 후 설치 명령·QR 을 본다. 그 토큰을 폐기하고 같은 명령으로 받아 본다. 고객사가 쓰는 [에이전트 설치] 화면에는 토큰 발급이 없다 — 거기서는 토큰 없이 받는다.',
        criteria: '발급하면 명령·QR 에 토큰이 채워진다. 폐기한 토큰으로는 설치 파일을 받을 수 없다.',
      },
      {
        id: 'agt-install-windows',
        verify: 'manual',
        title: 'Windows 기기가 한 줄 명령으로 설치된다',
        how: '새 Windows 기기에서 콘솔 [에이전트 설치] 의 한 줄 명령을 PowerShell 에 붙여 넣는다. **설치 파일만 받아 실행하는 길과 결과가 다르다** — 한 줄 명령에는 서버 주소가 실려 있고, 파일만 받아 깔면 첫 화면이 주소를 묻는다. 검사는 한 줄 명령 쪽으로 한다. Windows 기기가 없으면 해당 없음.',
        criteria: '사람 입력 없이 설치가 끝나고, 실행하면 서버 주소를 묻지 않고 페어링 코드가 뜬다.',
        where: 'field',
      },
      {
        id: 'agt-install-android',
        verify: 'manual',
        title: 'Android 기기가 QR · 설치 코드로 설치된다',
        how: '새 Android 기기에서 콘솔 [에이전트 설치] 의 QR 을 찍거나, 기기 브라우저 주소창에 콘솔 주소 뒤 /apk 를 넣어 APK 를 받아 설치한다. 넣을 토큰도 설치 코드도 없다 — 리모컨으로도 칠 수 있는 길이가 요점이다. Android 기기가 없으면 해당 없음.',
        criteria: '설치 후 서버 주소를 묻지 않고 페어링 코드가 뜬다(APK 에 주소가 박혀 나간다). 5.1 기기는 ‘알 수 없는 출처’ 허용이 필요하다는 것을 인계했다.',
        where: 'field',
      },
      {
        id: 'agt-version',
        verify: 'auto',
        title: '모든 기기가 배포 중인 에이전트 버전이다',
        how: '[에이전트] 롤아웃의 안정 버전과 [화면] 목록·화면 상세의 플레이어 버전을 대조한다.',
        criteria: '전 기기가 안정 버전과 같고 ‘뒤처짐’ 표시가 없다. 버전 고정한 기기가 있으면 이유를 비고에 적는다.',
      },
      {
        /*
         * 옛 `agt-auto-update` 를 갈음한다(2026-10-05). 기기 1대로만 보게 되어 있어, 기기 소유자가
         * 아니고 루트도 없는 안드로이드 5.1 이 "확인 필요" 에 멈추는 것을 시험 기기가 가렸다
         * (사전 시험 에뮬레이터에서 재현). 합격 조건이 달라졌으니 새 이름이다 — 옛 판정이 붙지 않게.
         */
        id: 'agt-auto-update-each',
        verify: 'manual',
        title: '에이전트가 사람 손 없이 새 버전으로 올라간다 — 납품 기기 종류마다',
        how: '납품 기기 종류(제조사·모델·OS 판)마다 1대씩 카나리 채널로 두고 [카나리로 지정] 으로 더 높은 버전을 지정한다. 정비 모드의 [지금 확인] 으로 앞당겨도 되지만 [설치]·[지금 업데이트] 는 누르지 않는다. 올릴 버전이 없으면 해당 없음.',
        criteria: '모든 종류에서 기기가 스스로 받아 설치하고 재생으로 돌아오며, 콘솔의 플레이어 버전이 새 버전으로 바뀐다. 화면 상세의 업데이트 단계가 ‘확인 필요’ 에서 멈추면 불합격이다 — 안드로이드는 기기 소유자(Device Owner)로 설정했거나, 루트(su)가 있고 에이전트가 0.7.7 이상이어야 사람 손 없이 설치된다(5.1 은 그 밖에는 설치 창도 재생 위로 띄우지 않는다).',
        critical: true,
        where: 'field',
      },
      {
        id: 'agt-kiosk',
        verify: 'manual',
        title: '재생 화면을 현장 사람이 벗어날 수 없다',
        how: '기기에서 뒤로 · 홈 · Alt+Tab · 작업 표시줄 · 화면 가장자리 쓸기를 시도한다.',
        criteria: '재생 화면이 전체 화면으로 유지되고 다른 앱으로 넘어가지 않는다. Android 는 기기 소유자(Device Owner) 설정이 아니면 잠기지 않으므로, 잠금이 필요한 현장이면 그 설정까지 확인한다.',
        where: 'field',
      },
    ],
  },

  /* ─────────────────────────────── 품질 ─────────────────────────────── */
  {
    id: 'playback',
    title: '재생 품질',
    intro: '콘솔이 “나가야 한다” 고 말하는 것과 화면에 실제로 나오는 것을 눈으로 대조한다.',
    items: [
      {
        id: 'play-live',
        verify: 'manual',
        title: '모든 화면이 편성대로 재생 중이다',
        how: '화면 앞에 서서 콘솔 [모니터링] 의 현재 재생과 한 대씩 대조한다.',
        criteria: '전 대수가 콘솔이 말하는 콘텐츠를 틀고 있다. 대기 화면·코드 화면·검은 화면인 기기가 없다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'play-video',
        verify: 'manual',
        title: '영상이 끊김 · 깨짐 없이 재생된다',
        how: '현장에서 쓸 가장 무거운 영상을 성능이 가장 낮은 기기에서 30분 이상 틀어 본다.',
        criteria: '멈춤·프레임 끊김·깨짐·소리만 나오는 증상이 없다. 첫 프레임에서 멈추면 그 기기가 못 푸는 코덱이다(구형 Android 는 H.264 High 프로파일에서 멈춘 사례가 있다).',
        critical: true,
        where: 'field',
      },
      {
        id: 'play-transition',
        verify: 'manual',
        title: '편성이 바뀔 때 검은 화면이 나오지 않는다',
        how: '아직 받지 않은 새 콘텐츠로 편성을 바꾸고 적용 과정을 화면 앞에서 본다.',
        criteria: '새 콘텐츠를 다 받을 때까지 이전 콘텐츠 또는 적용 진행 표시가 나오고, 검은 화면·깨진 이미지가 보이지 않는다.',
        where: 'field',
      },
      {
        id: 'play-quality',
        verify: 'manual',
        title: '이미지 · 템플릿이 선명하고 비율이 맞다',
        how: '실제 운영 콘텐츠를 화면에서 가까이, 그리고 현장 시청 거리에서 본다.',
        criteria: '늘어짐·잘림·흐림이 없고, 템플릿 글자가 시청 거리에서 읽힌다.',
        where: 'field',
      },
      {
        id: 'play-web',
        verify: 'manual',
        title: '웹 콘텐츠가 실제 화면에 표시된다',
        how: '등록한 웹 URL 콘텐츠를 화면에서 튼다. 웹 콘텐츠가 없으면 해당 없음.',
        criteria: '페이지 내용이 보인다. 빈 화면·로그인 요구·“연결이 거부됨” 이 뜨면 그 페이지가 삽입을 막는 것이다.',
        where: 'field',
      },
      {
        id: 'play-audio',
        verify: 'manual',
        title: '소리가 현장 요구대로 나온다',
        how: '소리를 쓰는 현장은 출력 장치와 음량을, 쓰지 않는 현장은 무음인지 확인한다.',
        criteria: '요구대로 나오거나 나오지 않는다. 음량 설정값을 비고에 적는다.',
        where: 'field',
      },
      {
        id: 'play-long-run',
        verify: 'manual',
        title: '24시간 무인 연속 재생을 통과했다',
        how: '전 화면을 최소 24시간(가능하면 72시간) 사람 손 없이 틀어 두고, 시작·종료 시각과 중간 이상을 기록한다. 매일 04시 새벽 재시작 구간을 포함한다. **끝나면 리포트를 시간대별로 갈라 본다** — 사람이 지켜보지 않은 시간의 구멍은 눈이 아니라 증빙에만 남는다.',
        criteria: '멈춤·검은 화면·앱 종료·코드 화면 복귀가 한 번도 없고, 리포트 재생 건수가 시간에 비례해 쌓였다. 04시 시간대의 재생 분량이 다른 시간대와 같다 — 에이전트 0.6.9 미만은 이 구간에서 1분마다 재시작해 시간당 15분이 빈다(2026-09-23 운영에서 찾은 결함).',
        critical: true,
        where: 'field',
      },
    ],
  },
  {
    id: 'performance',
    title: '성능 · 최적화',
    intro: '느려지고 무거워지는 문제는 기능 검사에서 드러나지 않는다. 시간을 재고, 자원 사용을 수치로 남긴다.',
    items: [
      {
        id: 'perf-propagation',
        verify: 'manual',
        title: '편성 변경이 45초 안에 화면에 반영된다',
        how: '이미 기기가 받아 둔 콘텐츠로 편성을 바꾸고, 저장 시각부터 화면이 바뀐 시각까지 3회 잰다. 최댓값을 비고에 적는다.',
        criteria: '3회 모두 45초 이내(폴링 30초 + 서버 캐시 10초 + 여유). 처음 받는 콘텐츠면 다운로드 시간과 적용 카운트다운 5초가 더해진다.',
        critical: true,
      },
      {
        id: 'perf-console',
        verify: 'manual',
        title: '콘솔 화면이 현장 망에서 빠르게 열린다',
        how: '인수처 사무실 PC 에서 브라우저 캐시를 비우고 로그인한 뒤 [대시보드] · [화면] · [콘텐츠] · [모니터링] 을 차례로 연다. 첫 방문과 재방문 시간을 적는다.',
        criteria: '첫 화면 5초 이내, 메뉴 이동 2초 이내. 전 화면을 부른 [화면] · [모니터링] 도 3초 안에 목록이 찬다.',
      },
      {
        id: 'perf-api',
        verify: 'auto',
        title: 'API 응답이 300ms 안에 온다',
        how: '개발자 도구 [네트워크] 에서 화면 목록(/api/v1/displays) · 화면 상세 · 콘텐츠 목록 요청의 시간을 본다. 또는 서버 망에서 curl -w "%{time_total}" 로 잰다.',
        criteria: '사내망 기준 각 요청이 300ms 이내(부하 목표 p99 < 300ms). 넘으면 서버 자원과 DB 를 먼저 본다.',
      },
      {
        id: 'perf-static-cache',
        verify: 'auto',
        title: '콘솔 정적 파일이 캐시되고 압축되어 나간다',
        how: '개발자 도구 [네트워크] 에서 assets/index-*.js 응답 헤더와 전송 크기를 첫 방문·재방문에 비교한다.',
        criteria: 'Cache-Control 이 immutable(또는 긴 max-age)이고 Content-Encoding 이 gzip 또는 br 이다. 재방문 때 다시 받지 않는다.',
      },
      {
        id: 'perf-media-spec',
        verify: 'auto',
        title: '콘텐츠 파일이 화면 규격에 맞게 최적화되어 있다',
        how: '등록한 운영 콘텐츠의 해상도 · 용량 · 코덱을 확인한다. 서버는 변환하지 않으므로 올린 파일이 그대로 화면에 간다.',
        criteria: '이미지·영상 해상도가 화면 해상도 이하, 영상은 H.264 + AAC 의 MP4, 1080p 기준 비트레이트 10Mbps 이하. 초과하는 파일은 줄여서 다시 올렸다.',
      },
      {
        id: 'perf-first-download',
        verify: 'manual',
        title: '전 화면이 편성을 받는 시간이 현장 회선에서 감당된다',
        how: '운영 편성 전체를 처음 배정하고 마지막 화면이 재생을 시작할 때까지 잰다. 편성 총용량 · 화면 수 · 소요 시간을 비고에 적는다.',
        criteria: '운영 시작 전에 끝날 수 있는 시간이다. 회선이 모자라면 편성 교체를 영업시간 밖에 하도록 인계했다.',
        where: 'field',
      },
      {
        id: 'perf-report-query',
        verify: 'auto',
        title: '리포트 조회가 기다릴 만한 시간 안에 끝난다',
        how: '[리포트] 에서 최근 30일과 90일을 조회해 시간을 잰다.',
        criteria: '30일 5초 이내. 90일이 오래 걸리면 운영 규모에서 더 느려진다는 것을 인계했다(집계는 원본 증빙을 매번 훑는다).',
      },
      {
        id: 'perf-server-memory',
        verify: 'auto',
        title: '서버 메모리가 오래 켜 두어도 늘어나지 않는다',
        how: '연속 재생 시험 시작과 끝에 [시스템] 의 메모리 · 가동 시간 · 편성 캐시 적중률을 적는다.',
        criteria: '24시간 뒤 메모리가 시작 대비 계속 늘어나는 추세가 없고 1GB 를 넘지 않는다. 가동 시간이 끊기지 않았다(도중 재시작 없음).',
      },
      {
        id: 'perf-device-resources',
        verify: 'manual',
        title: '기기 자원 사용이 안정적이다',
        how: '연속 재생 시험 시작과 끝에 기기의 메모리 · CPU · 저장 공간을 적는다(Windows 작업 관리자, Android 설정 ▸ 메모리 또는 adb).',
        criteria: '24시간 뒤 메모리가 계속 늘지 않고, 이미지 재생 중 CPU 가 상시 50% 를 넘지 않는다. 저장 공간 여유가 편성 총용량의 2배 이상이다(편성 교체 때 이전 것과 새 것을 함께 들고 있다).',
        where: 'field',
      },
    ],
  },
  {
    id: 'resilience',
    title: '장애 복구 · 내구성',
    intro: '이 시스템의 약속은 “서버가 죽어도 화면은 돈다” 이다. 약속을 검사에서 실제로 깨 본다.',
    items: [
      {
        id: 'res-offline-play',
        verify: 'manual',
        title: '네트워크를 끊어도 받아 둔 편성을 계속 재생한다',
        how: '재생 중인 기기의 랜선을 뽑거나 AP 를 끄고 10분 이상 본다.',
        criteria: '모든 항목이 계속 재생된다. 검은 화면·오류 화면·코드 화면이 뜨면 불합격.',
        critical: true,
        where: 'field',
      },
      {
        id: 'res-offline-reboot',
        verify: 'manual',
        title: '네트워크가 없는 채로 기기를 재부팅해도 재생한다',
        how: '네트워크를 끊은 상태에서 기기 전원을 껐다 켠다.',
        criteria: '사람 손 없이 받아 둔 편성으로 재생한다. Android 5.1 기기도 에이전트 0.6.4 부터 셸이 미디어를 디스크에 둬(docs/ANDROID.md §10) 통과한다 — 0.6.3 이하가 깔린 기기라면 먼저 올리고 검사한다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'res-reconnect',
        verify: 'manual',
        title: '네트워크가 돌아오면 저절로 다시 붙는다',
        how: '끊긴 동안 콘솔에서 편성을 바꾸고, 연결을 복구한 뒤 시간을 잰다.',
        criteria: '2분 안에 온라인으로 돌아오고, 끊긴 사이 바꾼 편성이 그 뒤 45초 안에 반영된다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'res-proof-backfill',
        verify: 'auto',
        title: '끊긴 동안의 재생 증빙이 복구 후 올라온다',
        how: '위 오프라인 시험 동안의 시간대를 복구 후 [리포트] 일자별 · 화면별로 조회한다.',
        criteria: '끊겨 있던 시간의 재생 건수가 빠짐없이 들어온다.',
      },
      {
        id: 'res-power-cut',
        verify: 'manual',
        title: '전원을 갑자기 끊었다 켜도 재생으로 돌아온다',
        how: '재생 중 기기 전원 코드를 뽑았다가 다시 꽂는다. 화면 패널 전원도 따로 끊어 본다.',
        criteria: '사람 손 없이 5분 안에 재생까지 간다. 절전·화면 꺼짐으로 빠지지 않는다.',
        critical: true,
        where: 'field',
      },
      {
        id: 'res-server-restart',
        verify: 'manual',
        title: '서버가 재기동하는 동안 화면이 멈추지 않는다',
        how: '서버를 재기동(또는 서버 호스트 재부팅)하고 그동안 화면을 지켜본다.',
        criteria: '재기동 중에도 화면이 캐시 편성으로 계속 나가고, 서버가 뜬 뒤 2분 안에 전 화면이 온라인으로 돌아온다.',
      },
      {
        id: 'res-bad-content',
        verify: 'manual',
        title: '재생이 안 되는 콘텐츠 하나가 전체를 멈추지 않는다',
        how: '기기가 못 트는 파일(예: 일부가 잘린 이미지)을 정상 콘텐츠와 함께 한 플레이리스트에 넣는다.',
        criteria: '그 항목만 건너뛰고 나머지는 계속 재생된다. 화면 상세 ‘마지막 오류’ 에 문제가 보고된다.',
      },
      {
        /*
         * 옛 `res-offline-boundary` 를 갈음한다(2026-10-05). 기기는 편성과 함께 앞으로의 구간을
         * 미리 받아 두고(`timetable`) 끊긴 채로도 경계에서 스스로 넘어가는데, 옛 기준은 "이전
         * 편성이 계속 나간다" 하나뿐이라 정상 동작을 불합격으로 읽게 했다(사전 시험). 설명했다는
         * 인계 문구도 걷었다 — 검사서는 검증만 담는다.
         */
        id: 'res-offline-timetable',
        verify: 'manual',
        title: '오프라인 중 시간대가 바뀌어도 화면이 비지 않는다',
        how: '두 경우를 본다. (가) 다음 시간대 규칙을 걸어 두고 기기가 받아 갈 때까지(45초) 기다린 뒤 망을 끊고 경계 시각을 넘긴다. (나) 망을 먼저 끊고 그 뒤에 다음 시간대 규칙을 바꾸고 경계 시각을 넘긴다.',
        criteria: '(가) 끊긴 채로도 경계 시각 1분 안에 다음 시간대 편성으로 넘어간다. (나) 받지 못한 편성으로는 넘어가지 않고 이전 편성이 계속 나간다. 두 경우 모두 검은 화면·오류 화면이 뜨지 않는다.',
        where: 'field',
      },
    ],
  },
  {
    id: 'security',
    title: '보안',
    intro: '설치할 때 잠깐 열어 둔 것과 검사에 쓴 것이 그대로 남아 납품되는 일을 막는다.',
    items: [
      {
        id: 'sec-bootstrap-token',
        verify: 'auto',
        title: '부트스트랩 관리 토큰을 제거했다',
        how: '서버 설정(.env)에 BOOTSTRAP_ADMIN_TOKEN 값이 남았는지 보고, 필요한 연동은 서비스 토큰으로 호출되는지 확인한다.',
        criteria: '값이 비어 있거나 줄이 없다. 연동이 서비스 토큰으로 동작한다.',
        critical: true,
      },
      {
        id: 'sec-ports',
        verify: 'manual',
        title: '외부에 열린 포트가 필요한 것뿐이다',
        how: '인수처 망 밖(또는 다른 망)에서 서버로 포트 점검을 하거나 방화벽 규칙을 확인한다.',
        criteria: 'HTTPS(443)와 합의한 관리 포트만 열려 있다. MongoDB(27017) · MinIO(9000·9001) · 서버 직결 포트는 밖에서 닿지 않는다.',
        critical: true,
      },
      {
        id: 'sec-media-signed',
        verify: 'auto',
        title: '미디어 파일을 로그인 없이 받을 수 없다',
        how: '콘텐츠 미리보기의 미디어 주소를 복사해, 서명 파라미터(X-Amz-…)를 지우거나 1시간 뒤에 로그아웃한 브라우저로 연다.',
        criteria: '거절(403 등)된다. 버킷이 공개로 열려 있지 않다.',
        critical: true,
      },
      {
        id: 'sec-origins',
        verify: 'auto',
        title: 'CORS 와 프레임 삽입 범위를 좁혔다',
        how: '서버 CORS_ORIGINS 와 리버스 프록시의 Content-Security-Policy frame-ancestors 를 확인한다.',
        criteria: 'CORS 는 실제로 쓰는 호스트만(같은 오리진이면 비움), 임베드하지 않으면 frame-ancestors 가 none 이다.',
      },
      {
        id: 'sec-login-paths',
        verify: 'auto',
        title: '로그인 경로를 의도대로 열고 닫았다',
        how: '서버 설정의 LOCAL_ACCOUNTS_ENABLED · IDENTITY_ISSUERS 를 확인한다.',
        criteria: '호스트 신원 연동 배포는 로컬 계정을 껐다. 로컬 계정만 쓰는 배포는 최초 시스템 계정 만들기 화면이 다시 뜨지 않는다.',
      },
      {
        id: 'sec-cleanup',
        verify: 'auto',
        title: '검사에 쓴 계정 · 토큰 · 기기를 정리했다',
        how: '[사용자] · [에이전트] 설치 토큰 목록 · 서비스 토큰 목록(API) · [화면] 목록에서 검사용으로 만든 것을 찾는다.',
        criteria: '검사용 계정은 삭제 또는 비활성, 설치 토큰은 폐기(그대로 두면 72시간 동안 설치 파일을 받을 수 있다), 쓰지 않는 서비스 토큰과 검사용 화면은 없다.',
        critical: true,
      },
    ],
  },
];

export const ALL_ITEMS: readonly InspectionItem[] = INSPECTION_SECTIONS.flatMap((section) => section.items);

export const VERIFY_LABEL = { auto: '자동', manual: '사람' } as const;
export const WHERE_LABEL = { field: '현장' } as const;

/**
 * 남은 항목을 **누가 판정하느냐로** 센다.
 *
 * 검사가 며칠에 걸쳐 끊기는 이유는 늘 같다 — 기기 앞에 서야 하는 항목을 뒤늦게 발견해 날을
 * 다시 잡는다. 남은 것을 이 셋으로 갈라 두면 "도구로 돌릴 것", "자리에서 사람이 볼 것",
 * "방문 날을 잡을 것" 이 먼저 보인다.
 */
export interface PendingByVerify {
  /** 도구가 같은 답을 낼 수 있다. */
  auto: number;
  /** 사람이 봐야 하지만 자리에서 된다(다른 망 접속·엑셀 열기 등). */
  manual: number;
  /** 사람이 기기·현장에 가야 한다. `manual` 에 포함되지 않는 별도의 수다. */
  field: number;
}

export function pendingByVerify(results: Results): PendingByVerify {
  const counts: PendingByVerify = { auto: 0, manual: 0, field: 0 };
  for (const item of ALL_ITEMS) {
    if (results[item.id]) continue;
    if (item.verify === 'auto') counts.auto += 1;
    else if (item.where === 'field') counts.field += 1;
    else counts.manual += 1;
  }
  return counts;
}

/** 항목 번호 — "3.4" 처럼 묶음 번호와 순번을 붙인다. 성적서에서 항목을 지목할 때 쓴다. */
export function itemNumber(sectionIndex: number, itemIndex: number): string {
  return `${sectionIndex + 1}.${itemIndex + 1}`;
}

export type Results = Readonly<Record<string, Verdict | undefined>>;

export interface Summary {
  total: number;
  judged: number;
  pass: number;
  fail: number;
  na: number;
  /** 필수 항목 중 불합격 수. 하나라도 있으면 종합 판정은 불합격이다. */
  criticalFail: number;
  /** 아직 판정하지 않은 항목 수. */
  pending: number;
  verdict: 'pass' | 'fail' | 'pending';
}

/**
 * 종합 판정.
 *
 * **미판정을 합격으로 올려 주지 않는다.** 검사표에서 가장 위험한 실수는 "안 본 것" 이
 * "문제없음" 으로 인쇄되어 나가는 것이다. 하나라도 남아 있으면 '검사 중' 이다.
 */
export function summarize(results: Results): Summary {
  let pass = 0;
  let fail = 0;
  let na = 0;
  let criticalFail = 0;

  for (const item of ALL_ITEMS) {
    const verdict = results[item.id];
    if (verdict === 'pass') pass += 1;
    else if (verdict === 'na') na += 1;
    else if (verdict === 'fail') {
      fail += 1;
      if (item.critical) criticalFail += 1;
    }
  }

  const total = ALL_ITEMS.length;
  const judged = pass + fail + na;
  const pending = total - judged;

  return {
    total,
    judged,
    pass,
    fail,
    na,
    criticalFail,
    pending,
    verdict: fail > 0 ? 'fail' : pending > 0 ? 'pending' : 'pass',
  };
}

export const SUMMARY_LABEL: Readonly<Record<Summary['verdict'], string>> = {
  pass: '합격',
  fail: '불합격',
  pending: '검사 중',
};
